import { z } from "zod";

// Optional-field helpers (ADR-0044 / ADV-1103).
//
// Each helper accepts undefined, null, and "" — the three "absence" forms that
// arrive from CSV imports and form submissions. Bare Zod primitives like
// z.string().uuid().optional() reject null and "", causing the entire row to
// fail the schema (the ADV-1022 / PR #738 bug class).
//
// Call site convention: append `.describe("Title Case Label")` LAST, e.g.
//   territory_id: optionalUuid().describe("Territory"),

export function optionalUuid() {
  return z
    .string()
    .uuid()
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null));
}

// Accepts undefined, null, and "". Empty-string coerces to undefined so callers
// that depend on a downstream `.default(...)` (or the absence of the key in the
// outgoing JSON payload) still get the default behavior; null passes through
// to the consumer so a caller can explicitly clear an enum value.
export function optionalEnum<T extends readonly [string, ...string[]]>(
  values: T,
) {
  return z
    .enum(values)
    .nullable()
    .optional()
    .or(z.literal("").transform(() => undefined));
}

// Variant for sites that already have a named Zod schema (an imported enum,
// or one with `.exclude(...)` applied). Same shape as optionalEnum — accepts
// undefined / null / "", coerces "" → undefined, preserves null.
export function optionalNamed<T extends z.ZodTypeAny>(schema: T) {
  return schema
    .nullable()
    .optional()
    .or(z.literal("").transform(() => undefined));
}

export function optionalEmail() {
  return z
    .string()
    .email()
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null));
}

export function optionalUrl() {
  return z
    .string()
    .url()
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null));
}

// True only for well-formed absolute URLs whose scheme is http: or https:.
// Anything else — javascript:, data:, vbscript:, file:, blob:, relative paths,
// or unparseable strings — returns false. Used to keep tenant-supplied URLs
// that get rendered as <a href> / <img src> from carrying an executable
// scheme (stored-XSS hardening, ADV-1254). Mirrors the http/https protocol
// gate in packages/services' SSRF guard (asset-pipeline/ssrf-guard.ts).
export function isHttpUrl(value: unknown): boolean {
  if (typeof value !== "string") return false;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  return parsed.protocol === "http:" || parsed.protocol === "https:";
}

// Required URL that must be absolute http(s). Rejects javascript:/data:/etc.
// `z.string().url()` ALONE accepts javascript:/data:/vbscript: (it only checks
// that `new URL()` parses), so the protocol refine is load-bearing, not
// redundant. Append `.describe("Title Case Label")` LAST at the call site.
export function httpUrl() {
  return z
    .string()
    .url()
    .refine(isHttpUrl, { message: "URL must use the http or https scheme" });
}

// External IDs are opaque tenant/source-supplied strings (e.g. Airtable
// "rec…" ids), NOT uuids — so optionalUuid() does not apply. Accepts
// undefined / null / "": "" coerces to null, null passes through so an
// absent FK reference validates and leaves the resolved column null. (ADV-1376)
// Uses union-first ordering (like optionalDateString) so the empty-string
// branch runs before the generic z.string() branch swallows "".
export function optionalExternalId() {
  return z
    .union([z.literal("").transform(() => null), z.string()])
    .nullable()
    .optional();
}

// CSV-friendly: strips $/,/whitespace before parsing. "" → null. "$NaN.00" → fail.
export function optionalNumber() {
  return z
    .union([
      z.number(),
      z.string().transform((s, ctx) => {
        const trimmed = s.trim().replace(/[$,\s]/g, "");
        if (trimmed === "") return null;
        const n = Number(trimmed);
        if (Number.isNaN(n)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Not a number: "${s}"`,
          });
          return z.NEVER;
        }
        return n;
      }),
    ])
    .nullable()
    .optional();
}

// CSV-friendly: yes/true/t/1 → true; no/false/f/0 → false; "" → null.
export function optionalBoolean() {
  return z
    .union([
      z.boolean(),
      z.string().transform((s, ctx) => {
        const v = s.trim().toLowerCase();
        if (v === "") return null;
        if (["true", "t", "yes", "y", "1"].includes(v)) return true;
        if (["false", "f", "no", "n", "0"].includes(v)) return false;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Not a boolean: "${s}"`,
        });
        return z.NEVER;
      }),
    ])
    .nullable()
    .optional();
}

// ISO date strings; "" → null. Use when a column is a date-as-string (no Date object).
// Implemented as a union so the empty-string branch runs BEFORE the generic string
// branch — a plain `.string().or(z.literal(""))` chain accepts "" as a valid string
// and never falls through to the .or() coercion.
export function optionalDateString() {
  return z
    .union([z.literal("").transform(() => null), z.string()])
    .nullable()
    .optional();
}
